// Ejemplos con placas sin USB: Arduino Pro Mini y ATtiny85 (con el adaptador USB-serie, con pilas y con una LCD I²C).
import { at, part, wires } from './helpers.js';

const BB = { id: 'bb1', type: 'breadboard', x: 0, z: 0, rot: 0, props: { size: 'half' } };

export const EXAMPLES = [
  {
    id: 'promini-ftdi',
    category: 'Placas',
    name: 'Arduino Pro Mini con adaptador USB-serie',
    description: 'La Pro Mini no tiene USB: el adaptador FTDI le da 5 V y lleva su Serial al monitor. Parpadea el LED STAT y obedece lo que escribes (1, 0 o b).',
    project: {
      name: 'Pro Mini con FTDI',
      files: {
        'sketch.ino': `// Arduino Pro Mini + adaptador USB-serie FTDI.
// La placa no tiene USB: el adaptador la alimenta (VCC y GND) y conecta su Serial con el monitor.
// Cables: TXD del adaptador al pin RXI (D0) de la placa y RXD del adaptador al pin TXO (D1).
// Escribe 1 para dejar encendido el LED STAT (pin 13), 0 para apagarlo y b para que parpadee.

const int LED = 13;
char modo = 'b';
bool encendido = false;
unsigned long ultimo = 0;
int cuenta = 0;

void setup() {
  pinMode(LED, OUTPUT);
  Serial.begin(9600);
  Serial.println(F("Pro Mini lista. Escribe 1, 0 o b."));
}

void loop() {
  if (Serial.available()) {
    char c = Serial.read();
    if (c == '1' || c == '0' || c == 'b') {
      modo = c;
      Serial.print(F("modo: "));
      Serial.println(c);
    }
  }
  if (modo == 'b') {
    if (millis() - ultimo >= 500) {
      ultimo = millis();
      encendido = !encendido;
      digitalWrite(LED, encendido);
      Serial.print(F("parpadeo "));
      Serial.println(++cuenta);
    }
  } else {
    digitalWrite(LED, modo == '1');
  }
}
`,
      },
      parts: [
        BB,
        part('mini', 'arduino-pro-mini', { version: '5v' }, at(BB, 'c4', 'arduino-pro-mini', { pin: 'RAW' })),
        part('ftdi', 'usb-serial', { level: '5v', baud: '9600' }, at(BB, 'a20', 'usb-serial', { pin: 'GND' })),
      ],
      wires: wires([
        ['bb1:b22', 'bb1:b7', 'red'], // VCC del adaptador a VCC de la placa
        ['bb1:b20', 'bb1:b5', 'black'], // GND con GND
        ['bb1:b23', 'bb1:j5', 'green'], // TXD del adaptador al RXI (D0) de la placa
        ['bb1:b24', 'bb1:j4', 'yellow'], // RXD del adaptador al TXO (D1) de la placa
      ]),
    },
  },
  {
    id: 'attiny85-pilas',
    category: 'Placas',
    name: 'ATtiny85 con pilas: parpadeo',
    description: 'Un ATtiny85 solo en la protoboard, alimentado por un portapilas de 3×AA (4.5 V), hace parpadear un LED en PB1. Sin alimentación el chip queda en reset.',
    project: {
      name: 'ATtiny85 con pilas',
      files: {
        'sketch.ino': `// ATtiny85 con pilas (ATTinyCore, reloj interno de 8 MHz).
// El chip no tiene USB: se alimenta con el portapilas (VCC y GND) y el programa ya está en su flash.
// Un LED con resistencia de 220 Ω en PB1 (pin 1), que es el LED_BUILTIN de ATTinyCore.

const int LED = 1;

void setup() {
  pinMode(LED, OUTPUT);
}

void loop() {
  digitalWrite(LED, HIGH);
  delay(300);
  digitalWrite(LED, LOW);
  delay(700);
}
`,
      },
      parts: [
        BB,
        part('tiny', 'attiny85', { clock: '8internal' }, at(BB, 'f10', 'attiny85')),
        part('r1', 'resistor', { value: 220 }, at(BB, 'a12', 'resistor')),
        part('led1', 'led', { color: 'red' }, at(BB, 'c15', 'led')),
        part('bat', 'battery', { voltage: '4.5' }, { x: 105, z: -22.86, rot: 180 }),
      ],
      wires: wires([
        ['bat:+', 'bb1:tp25', 'red'],
        ['bat:-', 'bb1:tn25', 'black'],
        ['bb1:a10', 'bb1:tp8', 'red'], // VCC del chip
        ['bb1:j13', 'bb1:tn10', 'black'], // GND del chip
        ['bb1:d15', 'bb1:tn12', 'black'], // cátodo del LED a GND
      ]),
    },
  },
  {
    id: 'attiny85-lcd',
    category: 'Placas',
    name: 'ATtiny85 + LCD I²C',
    description: 'El I²C del ATtiny85 sale por su USI (SDA en PB0, SCL en PB2): con la librería Wire de ATTinyCore y LiquidCrystal_I2C escribe en una LCD 16×2.',
    project: {
      name: 'ATtiny85 con LCD I2C',
      files: {
        'sketch.ino': `// ATtiny85 + LCD 16x2 con adaptador I2C (PCF8574, dirección 0x27).
// El ATtiny85 no tiene TWI: la librería Wire de ATTinyCore usa el USI, con SDA en PB0 y SCL en PB2.
// Se alimenta con un portapilas de 3xAA (4.5 V). Librería: LiquidCrystal_I2C.
#include <Wire.h>
#include <LiquidCrystal_I2C.h>

LiquidCrystal_I2C lcd(0x27, 16, 2);

void setup() {
  Wire.begin();
  lcd.init();
  lcd.backlight();
  lcd.setCursor(0, 0);
  lcd.print("ATtiny85 + I2C");
  lcd.setCursor(0, 1);
  lcd.print("Segundos:");
}

void loop() {
  lcd.setCursor(10, 1);
  lcd.print(millis() / 1000);
  delay(200);
}
`,
      },
      parts: [
        BB,
        part('tiny', 'attiny85', { clock: '8internal' }, at(BB, 'f10', 'attiny85')),
        part('lcd1', 'lcd-i2c', { size: '16x2', color: 'green', address: '0x27' }, { x: 60, z: -82, rot: 0 }),
        part('bat', 'battery', { voltage: '4.5' }, { x: 105, z: -22.86, rot: 180 }),
      ],
      wires: wires([
        ['bat:+', 'bb1:tp25', 'red'],
        ['bat:-', 'bb1:tn25', 'black'],
        ['bb1:a10', 'bb1:tp8', 'red'], // VCC del chip
        ['bb1:j13', 'bb1:tn10', 'black'], // GND del chip
        ['bb1:a13', 'lcd1:SDA', 'blue'], // PB0 = SDA
        ['bb1:a11', 'lcd1:SCL', 'yellow'], // PB2 = SCL
        ['bb1:tp15', 'lcd1:VCC', 'red'],
        ['bb1:tn15', 'lcd1:GND', 'black'],
      ]),
    },
  },
];
